import React from "react";
import { Modal } from "antd";
import "./QuickAddHelp.scss";

const REQUIRED_PARTS = [
  {
    token: "EUA",
    label: "contract",
    hint: "Ticker, alias or commodity name — EUA, API2, carbon",
  },
  {
    token: "apr27",
    label: "tenor",
    hint: "Month, quarter, season or year — apr27, Q1-27, Sum-27, Cal-28",
  },
  {
    token: "60/55cs",
    label: "strikes + option type",
    hint: "Short or full name — 60/55cs, 60/55 call spread, 60/55callspread",
  },
  {
    token: "x56",
    label: "underlying",
    hint: "Numeric UL with x — x56 / x56.5 — or live for unhedged. Anywhere in the line",
  },
];

const STRUCTURE_EXAMPLES = [
  { token: "100c", reads: "100 call — one strike, one leg" },
  { token: "95p", reads: "95 put" },
  { token: "60/55cs", reads: "call spread short name, glued" },
  { token: "123/23 call spread", reads: "full name, spaced" },
  { token: "123/23callspread", reads: "full name, glued, no spaces" },
  { token: "123/23 callspread", reads: "full name compacted after strikes" },
  { token: "100strad", reads: "straddle at 100" },
];

const SHORTCUTS = [
  { keys: "/", action: "Open or focus Quick Add" },
  { keys: "Tab", action: "Accept the grey suggestion" },
  { keys: "Esc", action: "Dismiss the suggestion" },
  { keys: "Enter", action: "Stage the line you just typed" },
  { keys: "Ctrl/⌘+Enter", action: "Submit to Pricer and Copy - no vol/prem" },
  { keys: "↑ / ↓", action: "Recall lines you staged before" },
  { keys: "Ctrl/⌘+Z", action: "Undo the last staged row" },
];

/**
 * How-to sheet for Quick Add (help icon in the panel title).
 */
const QuickAddHelp = ({ open, onClose, dark = false }) => (
  <Modal
    open={open}
    visible={open}
    title="Quick Add — How to use"
    onCancel={onClose}
    footer={null}
    width={560}
    destroyOnClose
    zIndex={1200}
    wrapClassName={
      dark ? "quick-add-help-wrap ant-modal-dark" : "quick-add-help-wrap"
    }
    className="quick-add-help"
    getContainer="#content-root"
  >
    <div className="quick-add-help__body">
      <section>
        <h4>Flow</h4>
        <ol>
          <li>
            Open with <b>/</b> on the Pricer, or the Quick Add button
          </li>
          <li>
            Type one option per line. Grey text is a suggestion — <b>Tab</b>{" "}
            accepts it
          </li>
          <li>
            <b>Enter</b> (or Add) stages the line into the table below.
            Incomplete lines show a warning and are not staged. While typing,
            a quiet muted hint (e.g. Need tenor) appears only after something
            is recognized — never a red banner until you press Enter
          </li>
          <li>
            Check the staged rows. Both buttons clear them after a successful
            send. <b>Ctrl/⌘+Enter</b> or{" "}
            <b>Submit to Pricer and Copy - no vol/prem</b> adds them to the
            Pricer and copies the structure-only shorthand.{" "}
            <b>Push to Team Run and Copy - no vol/prem</b> does that, then
            opens the same Team Run popup as the Pricer (trader, and whether
            the option is already on Team Run).
          </li>
        </ol>
      </section>

      <section>
        <h4>The four parts you must type</h4>
        <dl className="quick-add-help__parts">
          {REQUIRED_PARTS.map(({ token, label, hint }) => (
            <div key={label} className="quick-add-help__part">
              <dt>
                <code>{token}</code>
                <span className="quick-add-help__part-label">{label}</span>
              </dt>
              <dd>{hint}</dd>
            </div>
          ))}
        </dl>
        <p>
          Order does not matter —{" "}
          <code>x56 apr27 EUA 60/55cs</code> works just as well. Ratio and{" "}
          <code>offer</code> are optional.
        </p>
      </section>

      <section>
        <h4>Strikes + option type</h4>
        <p>
          Write the strike(s) first, then the strategy — short name or full
          option type, glued or spaced:
        </p>
        <ul className="quick-add-help__structures">
          {STRUCTURE_EXAMPLES.map(({ token, reads }) => (
            <li key={token}>
              <code>{token}</code>
              <span>{reads}</span>
            </li>
          ))}
        </ul>
        <p>
          Any strategy short name your desk uses is accepted — the list comes
          from the same strategies as Add Option. Give one strike per leg,
          separated by <code>/</code>.
        </p>
      </section>

      <section>
        <h4>Shortcuts</h4>
        <dl className="quick-add-help__keys">
          {SHORTCUTS.map(({ keys, action }) => (
            <div key={keys} className="quick-add-help__key-row">
              <dt>
                <kbd>{keys}</kbd>
              </dt>
              <dd>{action}</dd>
            </div>
          ))}
        </dl>
        <p>
          Paste several lines at once to stage them all. Lines that fail stay
          available in <b>↑</b> history so you can fix them.
        </p>
      </section>

      <section>
        <h4>Suggestions</h4>
        <p>
          As you type, a grey completion may appear for contracts, tenors
          (nearest tradeable year), strategy names, and whole lines you have
          staged before. It is ranked by what you use most on this browser.
          Ambiguous prefixes (e.g. <code>ju</code> — June or July) stay quiet on
          purpose.
        </p>
      </section>

      <section>
        <h4>Separators</h4>
        <ul>
          <li>
            A space between parts is enough: <code>EUA apr27 60/55cs</code>
          </li>
          <li>
            The tenor hyphen is optional: <code>feb27</code> ={" "}
            <code>feb-27</code>
          </li>
          <li>
            One tenor covers every leg; two tenors make a calendar:{" "}
            <code>Dec-27/Jun-27</code>
          </li>
          <li>
            Strikes use <code>/</code> and may be glued to the strategy:{" "}
            <code>60/55cs</code>
          </li>
        </ul>
      </section>

      <section>
        <h4>Optional extras</h4>
        <ul>
          <li>
            Uneven legs with a ratio: <code>60/55cs (1x2)</code>
          </li>
          <li>
            <code>offer</code> if you want sell-first legs instead of the
            default buy-first
          </li>
        </ul>
        <p>
          Buy/Sell defaults to buy-first — you do not need <code>bid</code>.
          Each option is priced from EST when added; a typed bid or ask is
          applied like a Pricer summary edit. Average, Cost of Carry and Risk
          Free can be edited in the table.
        </p>
      </section>

      <section>
        <h4>Staged table and Submit</h4>
        <ul>
          <li>
            Amber = we filled the value in for you — check it before Submit
          </li>
          <li>
            A tenor chip can read <b>expired?</b> when the tenor looks past
          </li>
          <li>Every cell is editable; rows can be duplicated or deleted</li>
          <li>
            If Submit fails, the bad cells turn red — hover one to read why
          </li>
          <li>Drag and resize the panel; its size and position are remembered</li>
        </ul>
      </section>

      <section>
        <h4>Examples</h4>
        <ul className="quick-add-help__examples">
          <li>
            <code>EUA apr27 12/24cs x56</code>
          </li>
          <li>
            <code>x56.5 eua feb-27 60/55 call spread</code>
          </li>
          <li>
            <code>apr27 EUA 100c live</code>
          </li>
          <li>
            <code>EUA Dec-27/Jun-27 85/75diagonal cs x80</code>
          </li>
        </ul>
      </section>
    </div>
  </Modal>
);

export default QuickAddHelp;
